import { Box, TextField } from '@mui/material';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'store/hooks';
import { CatalogWorkunit } from 'types/entities/CatalogWorkunit';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import Scope from 'types/entities/Scope';
import PurchaseOrders from 'types/entities/PurchaseOrders';
import { SelectedWorkunit } from 'types/entities/SelectedWorkunit';
import useOrderWorkunitApi from 'features/Orders/OrderWorkunits/services/useOrderWorkunitApi';
import './PurchaseOrderCell.scss';
import { formatDateToddMMYYY } from 'helpers/format';

type PurchaseOrderCellProps = {
  row: OrderWorkunit | CatalogWorkunit;
  disabled?: boolean;
  cellType: 'catalog' | 'selected';
};

const PurchaseOrderCell = (props: PurchaseOrderCellProps) => {
  const { row, disabled = false, cellType } = props;
  const [inputValue, setInputValue] = useState<any>(row.po_name?.name ?? '');
  /* const state = useSelector((state) => state.catalogToOrder); */
  const purchase_orders = useSelector((state) => state.purchaseOrder?.datas);

  const poList = purchase_orders
    ?.filter((item: { is_archived: boolean }) => item.is_archived === false)
    ?.map(
      (item: {
        is_valid: boolean;
        id: number;
        poref: string;
        is_archived: any;
        poend: string;
        reference: string;
        postart: string;
      }) => ({
        id: item.id,
        name: item.poref,
        is_valid: item.is_valid,
        reference: `${item.poref} (${formatDateToddMMYYY(item.postart)} - ${formatDateToddMMYYY(item.poend)})`,
      })
    );
  useEffect(() => {
    if (row.po_id === null && row.po_name === null && poList && poList.length > 0) {
      if (poList[0].is_valid === true) {
        const firstPo = poList[0];
        setInputValue(firstPo.reference);
        row.po_name = firstPo;
        row.po_id = firstPo.id;
      }
    }
  }, [row, poList]);

  const { changeWorkunitsPo } = useOrderWorkunitApi();
  const handleInputChange = (value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'clear' && value === '') {
      setInputValue('');
      changeWorkunitsPo(null, row.id, cellType, true);
    }
    setInputValue(value);
  };

  const onSelectPo = (purchaseorders: PurchaseOrders, rowId: number, reason?: string) => {
    if (purchaseorders) {
      changeWorkunitsPo(purchaseorders, rowId, cellType);
      setInputValue(purchaseorders.name);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        options={poList || []}
        disabled={disabled}
        autoHighlight
        onKeyDown={(event) => event.stopPropagation()}
        getOptionLabel={(option) => {
          const foundItem = poList?.find((item) => option.id === item.id);
          const reference = foundItem?.reference;
          option = {
            reference,
          };
          if (option && option.reference) {
            return option?.reference;
          }
          return ''; // Return an empty string or handle as needed when option.name is undefined
        }}
        isOptionEqualToValue={(option: any) => option.id === row.po_id}
        renderOption={(boxprops, option, { selected }) => (
          <Box component="li" {...{ ...boxprops }}>
            {option.reference}
          </Box>
        )}
        filterOptions={(options, params) => {
          const filtered: Scope[] = options.filter((option) =>
            option.name?.toLocaleLowerCase().includes(params.inputValue?.toLocaleLowerCase())
          );
          const filters = filtered.filter((scop) => scop.name !== 'not_attribued');
          return filters.filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          handleInputChange(newInputValue, reason);
        }}
        value={row.po_name}
        defaultValue={row.po_name}
        noOptionsText="No options"
        onChange={(e, value, reason) => handleSelectPo(onSelectPo, value, row, reason)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            fullWidth
            inputProps={{
              placeholder: 'Purchase order',
              autoComplete: '',
              ...params.inputProps,
            }}
            sx={{ '& .MuiInputBase-input': { fontSize: '.9rem' } }}
          />
        )}
      />
    </Box>
  );
};

export default (props: PurchaseOrderCellProps) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => <PurchaseOrderCell {...props} />, [props.row?.po_name]);

async function handleSelectPo(
  onSelectPo: (purchaseorders: PurchaseOrders, rowId: number, reason?: string) => void,
  value: PurchaseOrders | null,
  row: OrderWorkunit | CatalogWorkunit | SelectedWorkunit,
  reason: string
): Promise<void> {
  const isCustomWorkunit = (type: OrderWorkunit | CatalogWorkunit | SelectedWorkunit): type is SelectedWorkunit =>
    'randomId' in type;

  const rowId = isCustomWorkunit(row) ? row.randomId ?? row.id : row.id;
  const newValue = value?.poref?.startsWith('Add scope ') ? value : value;

  return onSelectPo(newValue as PurchaseOrders, rowId, reason);
}
